import React, { Component } from 'react';
import classNames from 'classnames';

import { EllipsisBox, Flex, Text } from 'core/components';
import { stopBubbling } from 'app/util/utils';
import TreeExpander from 'core/components/table/TreeExpander';
import CELL_TYPES from './CELL_TYPES';

function getColSpan(column, model, totalRow) {
  if (column.colSpan && typeof column.colSpan === 'function') {
    return column.colSpan(model, totalRow);
  }

  return column.colSpan || 1;
}

export function getColKey(col) {
  // Check the type last to cover action or checkbox columns
  return col?.id || col?.name || col?.key || col?.type;
}

export function isResizableColumn(col) {
  return col.resizable !== false && col.type !== CELL_TYPES.ACTION && col.key !== 'select';
}

export function getLastResizableColumnIndex(columns) {
  for (let i = columns.length - 1; i >= 0; i -= 1) {
    if (isResizableColumn(columns[i])) {
      return i;
    }
  }
  return -1;
}

export function getCellStyle({
  column,
  columns,
  index,
  model,
  totalRow,
  lastColumnPaddingRight = 12,
  collection,
  resizedColumns,
  columnWidthsRef
}) {
  const colSpan = getColSpan(column, model, totalRow);
  const defaultFlexBasis = 100;
  const lastResizableIndex = getLastResizableColumnIndex(columns);
  const isLastResizable = index === lastResizableIndex;
  const columnKey = getColKey(column);

  // check if anything obscures cell via colSpan if not a header row before applying resized widths
  if (model || totalRow) {
    for (let i = 0; i < index; i += 1) {
      if (i + getColSpan(columns[i], model, totalRow) - 1 >= index) {
        return {
          display: 'none'
        };
      }
    }
  }
  const userResizedWidth =
    (columnWidthsRef && columnWidthsRef[columnKey]) || (resizedColumns && resizedColumns[columnKey]);
  if (userResizedWidth) {
    // if cell has colSpan > 1, sum the resized widths of all spanned columns
    if (colSpan > 1) {
      let totalResizedWidth = userResizedWidth;
      for (let i = index + 1; i < index + colSpan && i < columns.length; i += 1) {
        const spannedCol = columns[i];
        const spannedKey = getColKey(spannedCol);
        const spannedWidth =
          (columnWidthsRef && columnWidthsRef[spannedKey]) || (resizedColumns && resizedColumns[spannedKey]);
        if (spannedWidth) {
          totalResizedWidth += spannedWidth;
        } else if (spannedCol.width) {
          totalResizedWidth += spannedCol.width;
        } else {
          // use default flex basis as fallback width
          totalResizedWidth += spannedCol.flexBasis || defaultFlexBasis;
        }
      }
      const style = {
        width: `${totalResizedWidth}px`,
        minWidth: `${totalResizedWidth}px`,
        maxWidth: `${totalResizedWidth}px`,
        flexBasis: `${totalResizedWidth}px`,
        flexGrow: 0,
        flexShrink: 0,
        paddingRight: column.paddingRight ?? (index + colSpan - 1 === columns.length - 1 ? lastColumnPaddingRight : 0),
        overflowWrap: column.wrapText ? 'break-word' : 'normal',
        position: 'relative'
      };
      return style;
    }

    // if last resizable column that was user-resized, use flexGrow: 1 to fill remaining space
    // user's resized width becomes the minimum width, but the column will grow to fill
    if (isLastResizable) {
      const style = {
        minWidth: `${userResizedWidth}px`,
        flexBasis: `${userResizedWidth}px`,
        flexGrow: 1,
        flexShrink: 0,
        paddingRight: column.paddingRight ?? (index === columns.length - 1 ? lastColumnPaddingRight : 0),
        overflowWrap: column.wrapText ? 'break-word' : 'normal',
        position: 'relative'
      };
      return style;
    }

    return {
      width: `${userResizedWidth}px`,
      minWidth: `${userResizedWidth}px`,
      maxWidth: `${userResizedWidth}px`,
      flexBasis: `${userResizedWidth}px`,
      flexGrow: 0,
      flexShrink: 0,
      paddingRight: column.paddingRight ?? (index === columns.length - 1 ? lastColumnPaddingRight : 0),
      overflowWrap: column.wrapText ? 'break-word' : 'normal',
      position: 'relative'
    };
  }

  // for cells with colSpan > 1, check if any of the spanned columns have resized widths
  // if so, we need to use fixed widths instead of flex-based calculation
  if (colSpan > 1 && (resizedColumns || columnWidthsRef)) {
    let hasAnyResizedWidth = false;
    let totalSpannedWidth = 0;

    for (let i = index; i < index + colSpan && i < columns.length; i += 1) {
      const col = columns[i];
      const colKey = getColKey(col);
      const resizedWidth = (columnWidthsRef && columnWidthsRef[colKey]) || (resizedColumns && resizedColumns[colKey]);

      if (resizedWidth) {
        hasAnyResizedWidth = true;
        totalSpannedWidth += resizedWidth;
      } else if (col.width) {
        totalSpannedWidth += col.width;
      } else if (col.actions) {
        totalSpannedWidth += 24 * col.actions.length + 24;
      } else {
        totalSpannedWidth += col.flexBasis || defaultFlexBasis;
      }
    }

    if (hasAnyResizedWidth) {
      return {
        width: `${totalSpannedWidth}px`,
        minWidth: `${totalSpannedWidth}px`,
        maxWidth: `${totalSpannedWidth}px`,
        flexBasis: `${totalSpannedWidth}px`,
        flexGrow: 0,
        flexShrink: 0,
        paddingRight: column.paddingRight ?? (index + colSpan - 1 === columns.length - 1 ? lastColumnPaddingRight : 0),
        overflowWrap: column.wrapText ? 'break-word' : 'normal',
        position: 'relative'
      };
    }
  }

  // Get this column's width/flex and if it has colSpan > 1, get from the columns it'll obscure
  let sumFlexBasis = 0;
  let sumWidth = 0;
  for (let i = index; i < index + colSpan; i += 1) {
    const col = columns[i];
    const colKey = getColKey(col);
    const colResizedWidth = (columnWidthsRef && columnWidthsRef[colKey]) || (resizedColumns && resizedColumns[colKey]);

    if (colResizedWidth) {
      sumWidth += colResizedWidth;
    } else if (col.width) {
      sumWidth += col.width;
    } else if (col.actions) {
      sumWidth += 24 * col.actions.length + 24;
    } else {
      sumFlexBasis += col.flexBasis || defaultFlexBasis;
    }
  }

  // Get the width/flex totals for all columns
  // We need to exclude widths of fixed-width columns that wind up flexed due to colSpans too.
  // This is why this atrocious looking 'colSpanBlocker' exists...
  // Also account for user-resized columns which should be treated as fixed widths.
  let totalFlexBasis = 0;
  let totalWidth = 0;
  let colSpanBlocker = -1;
  let colSpanWidth = 0;
  let colSpanHasFlex = false;
  for (let i = 0; i < columns.length; i += 1) {
    const col = columns[i];
    const colColSpan = getColSpan(columns[i], model, totalRow);
    const colKey = getColKey(col);
    const colResizedWidth = (columnWidthsRef && columnWidthsRef[colKey]) || (resizedColumns && resizedColumns[colKey]);

    if (colSpanBlocker < i) {
      if (!colSpanHasFlex && colSpanWidth > 0) {
        totalWidth += colSpanWidth;
      }

      if (colColSpan > 1) {
        colSpanBlocker = i + colColSpan - 1;
      }

      colSpanWidth = 0;
      colSpanHasFlex = false;
    }

    // check for user-resized width first (treat as fixed width)
    if (colResizedWidth) {
      if (colSpanBlocker < i) {
        totalWidth += colResizedWidth;
      } else {
        colSpanWidth += colResizedWidth;
      }
    } else if (col.width) {
      if (colSpanBlocker < i) {
        totalWidth += col.width;
      } else {
        colSpanWidth += col.width;
      }
    } else if (col.actions) {
      if (colSpanBlocker < i) {
        totalWidth += 24 * col.actions.length + 24;
      } else {
        colSpanWidth += 24 * col.actions.length + 24;
      }
    } else {
      totalFlexBasis += col.flexBasis || defaultFlexBasis;
      if (colSpanBlocker >= i) {
        colSpanHasFlex = true;
      }
    }
  }

  if (!colSpanHasFlex && colSpanWidth > 0) {
    totalWidth += colSpanWidth;
  }

  let minWidth;
  if (index === 0 && collection.isTreeGroupBy) {
    minWidth = (column.minWidth || 80) + 24 + (collection.maxTreeDepth || 0) * 12;
  }

  // last resizable column, use flexGrow: 1 to fill any gap
  if (isLastResizable) {
    const calculatedWidth =
      sumFlexBasis > 0 ? `calc((100% - ${totalWidth}px) * ${sumFlexBasis} / ${totalFlexBasis})` : sumWidth;
    return {
      minWidth: minWidth ?? column.minWidth ?? sumWidth ?? 'auto',
      width: calculatedWidth,
      flexGrow: 1,
      flexShrink: 0,
      paddingRight: column.paddingRight ?? (index === columns.length - 1 ? lastColumnPaddingRight : 0),
      overflowWrap: column.wrapText ? 'break-word' : 'normal',
      position: 'relative'
    };
  }

  return {
    minWidth: minWidth ?? column.minWidth ?? sumWidth ?? 'auto',
    width: sumFlexBasis > 0 ? `calc((100% - ${totalWidth}px) * ${sumFlexBasis} / ${totalFlexBasis})` : sumWidth,
    paddingRight: column.paddingRight ?? (index === columns.length - 1 ? lastColumnPaddingRight : 0),
    overflowWrap: column.wrapText ? 'break-word' : 'normal',
    position: 'relative'
  };
}

class Cell extends Component {
  shouldComponentUpdate(props) {
    const {
      value,
      loading,
      modelId,
      isSelected,
      isExpanded,
      column,
      columns,
      staticColumns,
      hasChildren,
      treeExpander
    } = this.props;

    return (
      value !== props.value ||
      loading !== props.loading ||
      isSelected !== props.isSelected ||
      isExpanded !== props.isExpanded ||
      hasChildren !== props.hasChildren ||
      treeExpander !== props.treeExpander ||
      (column.type === CELL_TYPES.ACTION && modelId !== props.modelId) ||
      (!staticColumns && columns !== props.columns)
    );
  }

  render() {
    const {
      column,
      model,
      rawValue,
      updateGrid,
      collection,
      loading,
      hasChildren,
      isExpanded,
      onToggle,
      treeExpander,
      tabPrefId
    } = this.props;

    const style = getCellStyle(this.props);
    const { flexDirection, justifyContent, alignItems, gap } = column;
    const verticalAlign = column.verticalAlign === 'top' ? 'flex-start' : column.verticalAlign || 'center';
    const horizontalAlign = column.align === 'right' ? 'flex-end' : column.align || 'flex-start';

    if (column.type === CELL_TYPES.ACTION) {
      const tdClassName = classNames('td actions', column.dynamicClassName ? column.dynamicClassName(model) : '', {
        select: column.key === 'select'
      });
      return (
        <Flex
          className={tdClassName}
          style={style}
          flexDirection={flexDirection}
          justifyContent={justifyContent || horizontalAlign}
          alignItems={alignItems || verticalAlign}
          p={column.padding}
          onClick={stopBubbling}
          onFocus={column.onFocus ? () => column.onFocus(model) : undefined}
          onMouseEnter={column.onMouseEnter ? () => column.onMouseEnter(model) : undefined}
          gap={gap}
          role="cell"
          data-col-key={getColKey(column)}
        >
          {column.actions.map((action) => action(model))}
        </Flex>
      );
    }

    const value = column.renderer
      ? column.renderer({ value: rawValue, column, model, updateGrid, collection, loading })
      : rawValue;
    const className = classNames(
      'td legend-table-cell',
      column.className,
      column.dynamicClassName ? column.dynamicClassName(model) : '',
      `${getColKey(column)}-cell`,
      {
        bracket: column.type === 'bracket'
      }
    );

    const rawTitle = rawValue ? rawValue.toString() : undefined;
    let displayTitle = column.title ? column.title({ model, value: rawValue }) : rawTitle;

    // rawValue of column can be an array, causing [object Object],[object Object]...
    if (`${displayTitle}`?.includes('[object Object]')) {
      displayTitle = undefined;
    }

    const defaultPadding = '6px 0px 6px 12px';
    const padding = column.padding ?? defaultPadding;

    return (
      <Flex
        className={className}
        style={style}
        flexDirection={flexDirection}
        flexWrap={column.wrapText ? 'wrap' : undefined}
        p={model.get('isOverlay') ? defaultPadding : padding}
        justifyContent={justifyContent || horizontalAlign}
        alignItems={alignItems || verticalAlign}
        gap={gap}
        overflow="hidden"
        role="cell"
        data-col-key={getColKey(column)}
      >
        {treeExpander && (
          <TreeExpander
            isExpanded={isExpanded}
            hasChildren={hasChildren}
            onToggle={onToggle}
            depth={model.treeDepth}
            small
            mr={1}
          />
        )}
        {tabPrefId && column.ellipsis !== false && (
          <EllipsisBox title={displayTitle} textAlign={column.align}>
            {value}
          </EllipsisBox>
        )}
        {!tabPrefId && column.ellipsis !== false && (
          <Text title={displayTitle} ellipsis>
            {value}
          </Text>
        )}
        {column.ellipsis === false && value}
      </Flex>
    );
  }
}

export default Cell;
